import { MutationOptions, WithRequired } from '@tanstack/react-query';

import {
  deleteSPOptimizerStoreExcludePhrase,
  deleteSPOptimizerStoreIncludePhrase,
  getSignedLogoUploadUrlForUser,
  getSignedMediaUploadUrlForUseCase,
  patchFlowMessageSetupSkippedReason,
  patchPretesterInboxName,
  patchSPOptimizerCampaignDeliverabilityBlockSetup,
  patchSPOptimizerFlowMessageExperiment,
  patchSPOptimizerStore,
  patchSPOptimizerStoreDeliverabilityBlockSetup,
  patchStoreInfo,
  patchUsecaseOnboardingState,
  postAddOrganizationMember,
  postAddPretesterInbox,
  postChangeUiEnabledForSPOptimizerStore,
  postCSBrowserSessionCancel,
  postCSBrowserSessionEnd,
  postCSBrowserSessionPresence,
  postCSBrowserSessionRenew,
  postCSBrowserSessionStart,
  postCreateSPOptimizerFlowMessageExperimentFromFlowMessage,
  postCreateSPOptimizerStoreDeliverabilityBlockSetup,
  postCreateSPOptimizerStoreFromFlowMessage,
  postCreateSPOptimizerStoreFromStore,
  postDisconnectKlaviyoAccount,
  postCreateUsecasesFromOnboarding,
  postDataQualityAssignDealStore,
  postDataQualityAssignProductLabel,
  postDataQualityAssignSubscriptionStore,
  postDataQualityLinkDealSubscription,
  postDeactivateSPOptimizerFlowMessageExperimentVariant,
  postDidUploadLogoForUser,
  postDidUploadUsecaseMedia,
  postDisableSPOptimizerFlowMessageExperiment,
  postEnableSPOptimizerFlowMessageExperiment,
  postFeedback,
  postRefreshExperimentUniversalBlock,
  postRemoveOrganizationMember,
  postSPOptimizerFlowMessageExperimentSetWinnerShard,
  postSlackAnnouncement,
  postSPOptimizerStoreBulkCreateNewFlowPhase,
  postSPOptimizerStoreExcludePhrase,
  postSPOptimizerStoreIncludePhrase,
  postSaveSelectedProduct,
  postSaveVideoScripts,
  postStoreInfo,
  postUpdateOrganizationMemberRole,
  regenerateDescriptionFromAboutPageUrl,
  setCampaignSetupAutomationPauseStatus,
} from './methods';

const makeMutation = <F extends (...a: [...Parameters<F>]) => Promise<Awaited<ReturnType<F>>>>(
  apiMethod: F,
  otherOptions?: Omit<MutationOptions<Awaited<ReturnType<F>>, unknown, Parameters<F>>, 'mutationFn'>,
): WithRequired<MutationOptions<Awaited<ReturnType<F>>, unknown, Parameters<F>>, 'mutationFn'> => {
  return {
    mutationFn: (args) => apiMethod(...args),
    ...otherOptions,
  };
};

export const mutations = {
  startCSBrowserSession: makeMutation(postCSBrowserSessionStart),
  endCSBrowserSession: makeMutation(postCSBrowserSessionEnd),
  cancelCSBrowserSession: makeMutation(postCSBrowserSessionCancel),
  renewCSBrowserSession: makeMutation(postCSBrowserSessionRenew),
  setCSBrowserSessionControl: makeMutation(postCSBrowserSessionPresence),
  signedLogoUploadUrlForUser: makeMutation(getSignedLogoUploadUrlForUser),
  signedMediaUploadUrlForUseCase: makeMutation(getSignedMediaUploadUrlForUseCase),
  didUploadUseCaseMedia: makeMutation(postDidUploadUsecaseMedia),
  didUploadLogoForUser: makeMutation(postDidUploadLogoForUser),
  usecaseOnboardingState: makeMutation(patchUsecaseOnboardingState),
  saveVideoScript: makeMutation(postSaveVideoScripts),
  createSPOptimizerStoreFromFlowMessage: makeMutation(postCreateSPOptimizerStoreFromFlowMessage),
  createSPOptimizerStoreFromStore: makeMutation(postCreateSPOptimizerStoreFromStore),
  createSPOptimizerStoreDeliverabilityBlockSetup: makeMutation(postCreateSPOptimizerStoreDeliverabilityBlockSetup),
  updateSPOptimizerStoreDeliverabilityBlockSetup: makeMutation(patchSPOptimizerStoreDeliverabilityBlockSetup),
  updateSPOptimizerStoreAboutPageUrl: makeMutation(regenerateDescriptionFromAboutPageUrl),
  createSPOptimizerStoreBulkCreateNewFlowPhase: makeMutation(postSPOptimizerStoreBulkCreateNewFlowPhase),
  createSPOptimizerFlowMessageExperimentFromFlowMessage: makeMutation(
    postCreateSPOptimizerFlowMessageExperimentFromFlowMessage,
  ),
  enableSPOptimizerFlowMessageExperiment: makeMutation(postEnableSPOptimizerFlowMessageExperiment),
  disableSPOptimizerFlowMessageExperiment: makeMutation(postDisableSPOptimizerFlowMessageExperiment),
  updateSPOptimizerFlowMessageExperimentSetWinnerShard: makeMutation(
    postSPOptimizerFlowMessageExperimentSetWinnerShard,
  ),
  updateSPOptimizerStoreUiEnabled: makeMutation(postChangeUiEnabledForSPOptimizerStore),
  disconnectKlaviyoAccount: makeMutation(postDisconnectKlaviyoAccount),
  createStoreInfo: makeMutation(postStoreInfo),
  createSPOptimizerStoreExcludePhrase: makeMutation(postSPOptimizerStoreExcludePhrase),
  deactivateSPOptimizerFlowMessageExperimentVariant: makeMutation(
    postDeactivateSPOptimizerFlowMessageExperimentVariant,
  ),
  createSPOptimizerStoreIncludePhrase: makeMutation(postSPOptimizerStoreIncludePhrase),
  deleteSPOptimizerStoreExcludePhrase: makeMutation(deleteSPOptimizerStoreExcludePhrase),
  deleteSPOptimizerStoreIncludePhrase: makeMutation(deleteSPOptimizerStoreIncludePhrase),
  updateStoreInfo: makeMutation(patchStoreInfo),
  updateSPOptimizerStore: makeMutation(patchSPOptimizerStore),
  updateSPOptimizerFlowMessageExperiment: makeMutation(patchSPOptimizerFlowMessageExperiment),
  createUseCasesFromOnboarding: makeMutation(postCreateUsecasesFromOnboarding),
  createFeedback: makeMutation(postFeedback),
  saveSelectedProduct: makeMutation(postSaveSelectedProduct),
  updateFlowMessageSetupSkippedReason: makeMutation(patchFlowMessageSetupSkippedReason),
  addPretesterInbox: makeMutation(postAddPretesterInbox),
  updatePretesterInboxName: makeMutation(patchPretesterInboxName),
  updateSPOptimizerCampaignDeliverabilityBlockSetup: makeMutation(patchSPOptimizerCampaignDeliverabilityBlockSetup),
  addOrganizationMember: makeMutation(postAddOrganizationMember),
  removeOrganizationMember: makeMutation(postRemoveOrganizationMember),
  updateOrganizationMemberRole: makeMutation(postUpdateOrganizationMemberRole),
  refreshExperimentUniversalBlock: makeMutation(postRefreshExperimentUniversalBlock),
  setCampaignSetupAutomationPauseStatus: makeMutation(setCampaignSetupAutomationPauseStatus),
  createSlackAnnouncement: makeMutation(postSlackAnnouncement),
  assignDataQualitySubscriptionStore: makeMutation(postDataQualityAssignSubscriptionStore),
  assignDataQualityDealStore: makeMutation(postDataQualityAssignDealStore),
  assignDataQualityProductLabel: makeMutation(postDataQualityAssignProductLabel),
  linkDataQualityDealSubscription: makeMutation(postDataQualityLinkDealSubscription),
  // oxlint-disable-next-line typescript/no-explicit-any
} satisfies Record<string, WithRequired<MutationOptions<any, unknown, any>, 'mutationFn'>>;
